/**
 * Panneau d'actions sur un bâtiment posé (§7).
 *
 * Quatre actions dans un seul écran : **améliorer, pivoter, déplacer, vendre**. Les
 * séparer en quatre gestes différents obligerait le joueur à retenir lequel fait quoi ;
 * réunies, elles se lisent comme un menu contextuel — la convention que tout joueur de
 * jeu de construction connaît déjà.
 *
 * **Le déplacement et la rotation sont gratuits, la vente rembourse à moitié.** Ce n'est
 * pas de l'équilibrage mais de la conception : c'est la composition de sa ville qui crée
 * l'attachement du joueur, donc le partage (§9). Faire payer un déplacement reviendrait à
 * taxer exactement ce qu'on cherche à encourager.
 */

import { n, t } from '@/i18n';
import type { CitySnapshot } from '@/core/gameState';
import { ECONOMY } from '@/config/balance';
import { buildingByType } from '@/config/buildings';
import { activeIsland, sellValue, upgradeCost } from '@/core/gameState';
import { isLandmark } from '@/config/landmarks';
import { buildingIncome } from '@/core/gameState';
import { quoteMaxLevel } from '@/core/maxLevel';
import { spriteUrl } from '@/render/spriteUrl';
import { createButton } from './components';
import type { ButtonHandle } from './components';
import { icon } from './icons';

export interface BuildingPanelCallbacks {
  readonly onUpgrade: (id: string) => void;
  /** Monter au palier maximum contre des gemmes (ROADMAP 2.5). */
  readonly onMaxLevel: (id: string) => void;
  readonly onRotate: (id: string) => void;
  readonly onMove: (id: string) => void;
  readonly onSell: (id: string) => void;
  readonly onClose: () => void;
}

export interface BuildingPanelHandle {
  readonly el: HTMLElement;
  open(snapshot: CitySnapshot, buildingId: string): void;
  close(): void;
  getSelected(): string | null;
  /** Rafraîchit les prix sans rouvrir : les pièces bougent dix fois par seconde. */
  update(snapshot: CitySnapshot): void;
  destroy(): void;
}

export function createBuildingPanel(callbacks: BuildingPanelCallbacks): BuildingPanelHandle {
  const el = document.createElement('section');
  el.className = 'building-panel v-panel v-panel--floating';
  el.hidden = true;

  const title = document.createElement('h3');
  title.className = 'building-panel__title';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'v-icon-btn building-panel__close';
  close.setAttribute('aria-label', t('common.close'));
  close.append(icon('close'));
  close.addEventListener('click', () => {
    callbacks.onClose();
  });

  /*
    ── CE QUE CE BÂTIMENT-LÀ RAPPORTE ────────────────────────────────────────

    « Quand on clique sur un bâtiment il faut voir combien il nous rapporte » (Benoit,
    21 août). Le panneau annonçait le PRIX de l'amélioration et celui de la revente, mais
    jamais le revenu — c'est-à-dire la seule raison pour laquelle le bâtiment est là.

    ⚠️ Le chiffre est celui de CET exemplaire, prestige et paliers compris, pas la
    production de base du catalogue : un joueur d'ère 4 avec un bâtiment au palier 3 ne
    reconnaîtrait pas sa ville dans le nombre du catalogue. Il est dérivé de la même
    fonction que le HUD (`buildingIncome`), donc les deux ne peuvent pas diverger.
  */
  const income = document.createElement('p');
  income.className = 'building-panel__income';

  const head = document.createElement('div');
  head.className = 'building-panel__head';
  head.append(title, close);

  let selected: string | null = null;

  const upgrade: ButtonHandle = createButton({
    label: t('building.upgrade'),
    variant: 'primary',
    icon: 'star',
    onClick: () => {
      if (selected !== null) callbacks.onUpgrade(selected);
    },
  });

  /*
    ── LE BOUTON QUI NE SE TAIT PLUS (ROADMAP 2.5) ───────────────────────────

    « Améliorer » se désactive dès que les pièces manquent, et ne dit rien de plus.
    C'est le défaut que la 1.3 a corrigé dans la boutique — « plus de bouton non
    cliquable », demande de Benoit — et il est resté entier ici, là où le joueur passe
    le plus de temps une fois son terrain plein.

    Le bouton n'apparaît QUE lorsqu'il a quelque chose à offrir : bâtiment pas au
    maximum, et pièces insuffisantes. Un joueur qui peut déjà payer en pièces n'a rien
    à faire d'une offre en gemmes — la lui montrer serait vendre ce qu'il possède.
  */
  const maxLevel: ButtonHandle = createButton({
    label: t('building.maxLevel'),
    variant: 'secondary',
    icon: 'gem',
    onClick: () => {
      if (selected !== null) callbacks.onMaxLevel(selected);
    },
  });

  // Une icône = un sens (D§6) : « pivoter » portait un chevron, « déplacer » un plus —
  // deux signes qui mentaient sur les deux gestes les plus fréquents du panneau.
  const rotate: ButtonHandle = createButton({
    label: t('building.rotate'),
    variant: 'secondary',
    icon: 'rotate',
    onClick: () => {
      if (selected !== null) callbacks.onRotate(selected);
    },
  });

  const move: ButtonHandle = createButton({
    label: t('building.move'),
    variant: 'secondary',
    icon: 'move',
    onClick: () => {
      if (selected !== null) callbacks.onMove(selected);
    },
  });

  const sell: ButtonHandle = createButton({
    label: t('building.sell'),
    variant: 'ghost',
    icon: 'coin',
    onClick: () => {
      if (selected !== null) callbacks.onSell(selected);
    },
  });

  const hint = document.createElement('p');
  hint.className = 'building-panel__hint';

  /*
    Ce que l'amélioration DONNE À VOIR. Un palier est un MODÈLE différent, jamais un
    agrandissement (D§6) — mais le panneau n'en montrait rien : le joueur payait pour
    découvrir. La vignette du palier suivant vend l'amélioration mieux qu'un chiffre.
  */
  const nextPreview = document.createElement('img');
  nextPreview.className = 'building-panel__next';
  nextPreview.alt = '';
  nextPreview.setAttribute('aria-hidden', 'true');
  nextPreview.hidden = true;

  const hintRow = document.createElement('div');
  hintRow.className = 'building-panel__hint-row';
  hintRow.append(nextPreview, hint);

  const actions = document.createElement('div');
  actions.className = 'building-panel__actions';
  /*
    ⚠️ CHAQUE ACTION PORTE SON NOM, et ce n'est pas cosmétique.

    Le parcours enchaîné visait `.building-panel__actions .v-btn` en position 1 pour
    « Pivoter » et en dernière position pour « Vendre ». L'arrivée du bouton « Niveau
    max » (2.5) a décalé la rangée : la sonde a tapé sur un bouton MASQUÉ, sans boîte
    englobante, et a échoué sur « cible sans boîte englobante » — un symptôme muet à
    vingt lignes de la cause (run 32357628191).

    Un sélecteur positionnel est une supposition sur l'ordre du DOM (règle 22). On nomme
    donc les cinq actions, et l'ordre peut changer sans casser personne.
  */
  upgrade.el.classList.add('building-panel__upgrade');
  maxLevel.el.classList.add('building-panel__max');
  rotate.el.classList.add('building-panel__rotate');
  move.el.classList.add('building-panel__move');
  sell.el.classList.add('building-panel__sell');

  actions.append(upgrade.el, maxLevel.el, rotate.el, move.el, sell.el);

  el.append(head, income, hintRow, actions);

  function refresh(snapshot: CitySnapshot): void {
    if (selected === null) return;

    const building = activeIsland(snapshot).buildings.find((b) => b.id === selected);
    if (building === undefined) {
      callbacks.onClose();
      return;
    }

    title.textContent = t(`buildings.${building.type}` as never);

    const maxed = building.level >= 3;
    const cost = upgradeCost(snapshot, selected);

    // Le bouton dit ce qu'il coûte, toujours : un prix caché derrière un clic est un
    // prix qu'on découvre en le payant.
    /*
      ── UN POINT DE REPÈRE N'A NI PALIERS NI VALEUR DE REVENTE ────────────────

      Le panneau affichait « Améliorer · 0 » et « Vendre · +0 » sur un objet qui refuse
      l'amélioration et ne rend pas de pièces — il a coûté des GEMMES. Deux boutons qui
      annoncent une action impossible, et un prix de zéro qui se lit comme une panne
      (rapport de Benoit, 21 août).

      Les deux boutons de palier disparaissent, et « Vendre » devient « Retirer » : c'est
      exactement ce que le geste fait. Il rend la case, pas les gemmes — les rembourser
      permettrait de poser, retirer, reposer indéfiniment.
    */
    const landmark = isLandmark(building.type);

    /*
      Un repère ne produit pas « son » nombre : il ajoute une part de ce que la ville
      produit déjà (`LANDMARK_SHARE`). On le dit autrement plutôt que d'afficher un
      chiffre qui semblerait venir du bâtiment lui-même.
    */
    const perSecond = buildingIncome(snapshot, building);
    /*
      Un repère dont la contribution s'arrondit à zéro — ville naissante, où sa part de
      1,3 % ne vaut presque rien — n'affiche pas « 0,0 » : c'est exact et illisible. On dit
      alors ce qu'il FERA, ce qui est de toute façon son argument à ce moment-là.
    */
    income.textContent = landmark
      ? perSecond >= 0.05
        ? t('building.incomeShare', { value: n(perSecond) })
        : t('building.incomeGrows')
      : t('building.income', { value: n(perSecond) });
    upgrade.el.hidden = landmark;
    sell.setLabel(
      landmark
        ? t('building.remove')
        : t('building.sellFor', { value: n(sellValue(snapshot, selected)) }),
    );

    upgrade.setLabel(maxed ? t('building.maxTier') : t('building.upgradeCost', { value: n(cost) }));
    upgrade.setDisabled(maxed || snapshot.coins < cost);

    /*
      L'offre en gemmes se DÉRIVE, elle ne se devine pas : `quoteMaxLevel` porte le
      prix, le nombre de paliers et la raison d'un refus. On n'affiche le bouton que
      lorsqu'un achat est réellement possible — un bouton visible mais refusé serait
      exactement le bouton muet qu'on vient de supprimer.
    */
    const quote = quoteMaxLevel(snapshot, selected, Date.now());
    const price = quote.refusal === null ? quote.gems : null;
    maxLevel.el.hidden = price === null || landmark;
    if (price !== null) {
      maxLevel.setLabel(t('building.maxLevelFor', { value: n(price) }));
    }

    // Le libellé de vente est posé plus haut : un repère dit « Retirer », les autres
    // annoncent ce qu'ils rendent.

    /*
      Ce que l'amélioration RAPPORTE, pas seulement ce qu'elle coûte.

      Un bouton qui n'annonce qu'un prix demande au joueur de payer pour découvrir
      l'effet. On affiche donc le gain de production, calculé sur le multiplicateur de
      palier réel : c'est ce qui transforme l'amélioration en décision au lieu d'un pari.
    */
    const definition = buildingByType(building.type);
    const factors = ECONOMY.tierMultipliers;
    const current = factors[Math.min(2, building.level - 1)] ?? 1;
    const next = factors[Math.min(2, building.level)] ?? current;
    const gain = definition === undefined ? 0 : definition.baseProduction * (next - current);

    hint.textContent = maxed
      ? t('building.tierMax', { value: String(building.level) })
      : t('building.tierGain', { value: String(building.level), gain: n(gain) });

    // La vignette du palier suivant — le modèle de base du palier, celui que le
    // pipeline garantit rendu pour chaque type (`${type}-t${n}-thumb.png`).
    nextPreview.hidden = maxed;
    if (!maxed) {
      nextPreview.src = spriteUrl(
        `${building.type}-t${String(Math.min(3, building.level + 1))}-thumb.png`,
      );
    }
  }

  return {
    el,
    open(snapshot, buildingId) {
      selected = buildingId;
      el.hidden = false;
      refresh(snapshot);
    },
    close() {
      selected = null;
      el.hidden = true;
    },
    getSelected: () => selected,
    update: refresh,
    destroy() {
      el.remove();
    },
  };
}
